import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { BadgeCheck, BookOpen, Check, Heart, MessageCircle, Share2, ArrowLeft, Plus, MoreHorizontal, ChevronDown } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import FloatingDock from '@/components/layout/FloatingDock';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ThreadPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch (err) {
        navigate('/auth');
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    const fetchThread = async () => {
      try {
        const response = await axios.get(`${API_URL}/posts/threads/${threadId}`, {
          withCredentials: true
        });
        setThread(response.data);
      } catch (error) {
        toast.error('Failed to load thread');
      } finally {
        setLoading(false);
      }
    };

    if (threadId) {
      fetchThread();
    }
  }, [threadId]);

  const addToThread = async () => {
    if (!newPostContent.trim()) return;
    setAdding(true);

    try {
      // First create the post
      const postRes = await axios.post(`${API_URL}/posts`, {
        content: newPostContent,
        thread_id: threadId
      }, { withCredentials: true });

      // Refresh thread
      const threadRes = await axios.get(`${API_URL}/posts/threads/${threadId}`, {
        withCredentials: true
      });
      setThread(threadRes.data);
      setNewPostContent('');
      setShowAddForm(false);
      toast.success('Added to thread!');
    } catch (error) {
      toast.error('Failed to add to thread');
    } finally {
      setAdding(false);
    }
  };

  const completeThread = async () => {
    try {
      await axios.post(`${API_URL}/posts/threads/${threadId}/complete`, {}, {
        withCredentials: true
      });
      setThread(prev => ({ ...prev, is_complete: true }));
      toast.success('Thread marked as complete');
    } catch (error) {
      toast.error('Failed to complete thread');
    }
  };

  const likeThread = async () => {
    try {
      await axios.post(`${API_URL}/posts/threads/${threadId}/like`, {}, {
        withCredentials: true
      });
      setThread(prev => ({ ...prev, likes_count: (prev.likes_count || 0) + 1, liked: true }));
    } catch (error) {
      // Already liked, try to unlike
      try {
        await axios.delete(`${API_URL}/posts/threads/${threadId}/like`, {
          withCredentials: true
        });
        setThread(prev => ({ ...prev, likes_count: Math.max(0, (prev.likes_count || 1) - 1), liked: false }));
      } catch (e) {
        toast.error('Failed to update like');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex flex-col items-center justify-center">
        <BookOpen className="w-16 h-16 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-600 dark:text-gray-400">Thread not found</h2>
        <Button onClick={() => navigate('/feed')} className="mt-4">Go to Feed</Button>
      </div>
    );
  }

  const isAuthor = user?.user_id === thread.user_id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-bold text-lg flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-500" />
                Thread
              </h1>
              <p className="text-sm text-gray-500">{thread.posts_count} posts</p>
            </div>
          </div>
          
          {thread.is_complete && (
            <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium flex items-center gap-1">
              <Check className="w-4 h-4" /> Complete
            </span>
          )}
        </div>
      </header>

      {/* Thread Content */}
      <main className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Thread Author */}
        <div className="flex items-center gap-3 mb-6">
          <Avatar className="w-12 h-12">
            <AvatarImage src={thread.user?.avatar} />
            <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
              {thread.user?.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold">{thread.user?.name}</span>
              {thread.user?.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
            </div>
            <span className="text-gray-500 text-sm">@{thread.user?.username}</span>
          </div>
          <span className="text-sm text-gray-400 ml-auto">
            {formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}
          </span>
        </div>

        {/* Thread Posts */}
        <div className="space-y-0">
          {thread.posts_data?.map((post, idx) => (
            <div key={post.post_id} className="relative" data-testid={`thread-post-${idx}`}>
              {/* Thread line */}
              {idx < thread.posts_data.length - 1 && (
                <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-gradient-to-b from-purple-400 to-purple-200 dark:from-purple-600 dark:to-purple-900" />
              )}
              
              <Card className="p-5 mb-0 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 relative">
                {/* Post number */}
                <div className="absolute -left-3 top-4 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 flex items-center justify-center text-sm font-bold border-2 border-white dark:border-gray-900">
                  {idx + 1}
                </div>
                
                <div className="pl-6">
                  <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap mb-3">
                    {post.content}
                  </p>

                  {/* Media */}
                  {post.video_url && (
                    <div className="relative rounded-xl overflow-hidden bg-black mb-3">
                      <video
                        src={`${process.env.REACT_APP_BACKEND_URL}${post.video_url}`}
                        className="w-full max-h-72 object-contain"
                        controls
                      />
                    </div>
                  )}

                  {post.images && post.images.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {post.images.slice(0, 4).map((img, i) => (
                        <img key={i} src={img} alt="" className="rounded-xl w-full object-cover" />
                      ))}
                    </div>
                  )}

                  {post.audio_url && (
                    <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
                      <audio
                        src={`${process.env.REACT_APP_BACKEND_URL}${post.audio_url}`}
                        controls
                        className="w-full h-10"
                        preload="metadata"
                      />
                    </div>
                  )}

                  {post.image_url && (!post.images || post.images.length === 0) && (
                    <img src={post.image_url} alt="" className="rounded-xl w-full object-cover max-h-64 mb-3" />
                  )}

                  {post.gif_url && (
                    <img src={post.gif_url} alt="GIF" className="max-h-48 rounded-xl mb-3" />
                  )}

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                    <button 
                      onClick={() => navigate(`/post/${post.post_id}`)}
                      className="text-primary hover:underline"
                    >
                      View post →
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>

        {/* Add to Thread (Author only, thread not complete) */}
        {isAuthor && !thread.is_complete && (
          <div className="mt-6">
            {showAddForm ? (
              <Card className="p-4 rounded-2xl border-2 border-purple-500/50 bg-purple-50/50 dark:bg-purple-900/20">
                <Textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder="Continue your thread..."
                  className="mb-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                  rows={3}
                />
                <div className="flex justify-between">
                  <Button variant="ghost" onClick={() => setShowAddForm(false)}>Cancel</Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={completeThread}>
                      <Check className="w-4 h-4 mr-1" /> Mark Complete
                    </Button>
                    <Button onClick={addToThread} disabled={adding || !newPostContent.trim()}>
                      {adding ? 'Adding...' : 'Add to Thread'}
                    </Button>
                  </div>
                </div>
              </Card>
            ) : (
              <Button
                onClick={() => setShowAddForm(true)}
                className="w-full py-6 rounded-2xl border-2 border-dashed border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/40"
              >
                <Plus className="w-5 h-5 mr-2" /> Continue Thread
              </Button>
            )}
          </div>
        )}

        {/* Thread Actions */}
        <div className="mt-8 flex items-center justify-center gap-6">
          <Button
            variant="ghost"
            size="lg"
            onClick={likeThread}
            className={`rounded-full ${thread.liked ? 'text-red-500' : ''}`}
          >
            <Heart className={`w-6 h-6 mr-2 ${thread.liked ? 'fill-current' : ''}`} />
            {thread.likes_count || 0}
          </Button>
          
          <Button variant="ghost" size="lg" className="rounded-full">
            <MessageCircle className="w-6 h-6 mr-2" />
            Reply
          </Button>
          
          <Button variant="ghost" size="lg" className="rounded-full">
            <Share2 className="w-6 h-6 mr-2" />
            Share
          </Button>
        </div>
      </main>

      <FloatingDock />
    </div>
  );
};

export default ThreadPage;
